import { FileText, Download, Phone } from "lucide-react";
import { Reveal } from "@/components/Reveal";
import { agreementDownloadUrl, formatBytes } from "@/lib/api";

const STEPS = [
  {
    n: "01",
    title: "Download the agreement",
    copy: "Review and sign the client agreement — it covers scope, filings, payment and claims in plain language.",
  },
  {
    n: "02",
    title: "Send it with your last invoice",
    copy: "Return the signed agreement with your DOT/MC authority and the invoice from your current insurer.",
  },
  {
    n: "03",
    title: "We countersign and bind",
    copy: "Coverage bound, filings submitted, certificates issued — often the very same day.",
  },
];

export const BecomeClient = ({ settings }) => {
  const agreement = settings?.agreement || { source: "default", filename: "Fleetguard-Client-Agreement.pdf", size: 0 };
  const phone = settings?.phone || "(555) 210-4400";

  return (
    <section id="become-client" data-testid="become-client-section" className="bg-brand-navy py-16 sm:py-24">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-2 lg:gap-20 lg:px-8">
        <div>
          <Reveal>
            <p className="eyebrow text-blue-300">Become a Client</p>
            <h2 className="mt-4 font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-[2.75rem] lg:leading-[1.1]">
              On the road with Fleetguard in three steps.
            </h2>
          </Reveal>
          <Reveal delay={0.12}>
            <div className="mt-10 space-y-8">
              {STEPS.map((s) => (
                <div key={s.n} className="flex gap-5" data-testid="client-step">
                  <span className="font-mono text-sm font-semibold text-brand-orange">{s.n}</span>
                  <div className="border-l border-white/15 pl-5">
                    <h3 className="font-display text-lg font-bold text-white">{s.title}</h3>
                    <p className="mt-1 text-[15px] leading-relaxed text-white/65">{s.copy}</p>
                  </div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="rounded-3xl bg-white p-8 shadow-2xl shadow-black/30 sm:p-10" data-testid="agreement-download-card">
            <div className="flex items-start gap-4">
              <span className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-brand-ice text-brand-blue">
                <FileText className="h-7 w-7" />
              </span>
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-400">Client document</p>
                <h3 className="mt-1 font-display text-2xl font-bold tracking-tight text-brand-navy">
                  Client Insurance Agreement
                </h3>
                <p className="mt-1 font-mono text-xs text-slate-500" data-testid="agreement-meta">
                  {agreement.filename}
                  {agreement.size ? ` · ${formatBytes(agreement.size)}` : ""}
                  {agreement.source === "default" ? " · sample" : ""}
                </p>
              </div>
            </div>

            <div className="mt-7 h-px bg-slate-100" />

            <ul className="mt-7 space-y-3 text-sm text-slate-600">
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" /> Plain-language terms — scope, filings, payment, claims
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" /> Sign, scan, email — no notary required
              </li>
              <li className="flex items-center gap-2">
                <span className="h-1.5 w-1.5 rounded-full bg-brand-blue" /> Include your last invoice to unlock up to 50% off
              </li>
            </ul>

            <a
              href={agreementDownloadUrl}
              data-testid="become-client-download-btn"
              className="mt-8 flex w-full items-center justify-center gap-2 rounded-full bg-brand-blue px-6 py-4 text-base font-semibold text-white shadow-lg shadow-blue-600/25 transition-all hover:-translate-y-0.5 hover:bg-brand-bluedeep"
            >
              <Download className="h-5 w-5" /> Download Agreement
            </a>

            <p className="mt-4 flex items-center justify-center gap-2 text-center text-xs text-slate-500">
              <Phone className="h-3.5 w-3.5" /> Questions? Call {phone} — a licensed agent answers.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};
